import { z } from 'zod';
import { PERSONA_REFERENCE_VIDEO_ANALYSIS_PROMPT_USED_IN, PERSONA_SCRIPT_IMPORT_PROMPT_USED_IN } from '../events/persona-opportunities.js';
import { CHANGELOG_PROMPT_DEPLOY_NOTES, CHANGELOG_PROMPT_ROUNDUP } from './changelog-prompts.js';
import {
  SCRIPT_REVIEW_PROMPT_ITERATION_TEXT_OVERLAY,
  SCRIPT_REVIEW_PROMPT_ITERATION_VISUAL,
  SCRIPT_REVIEW_PROMPT_ITERATION_VOICEOVER,
  SCRIPT_REVIEW_PROMPT_SCRIPT_EXTRACTION,
  SCRIPT_REVIEW_PROMPT_SCRIPT_MATCH,
  SCRIPT_REVIEW_PROMPT_SUGGESTION,
  SCRIPT_REVIEW_PROMPT_TEXT_OVERLAY,
  SCRIPT_REVIEW_PROMPT_TRANSCRIPT,
  SCRIPT_REVIEW_PROMPT_VISUAL,
  SCRIPT_REVIEW_PROMPT_VOICEOVER,
} from './script-review-prompts.js';
import {
  PromptTaskTypeSchema,
  derivePromptModelCapabilities,
  type PromptTaskType,
} from './prompt-task-types.js';

// ─── Catalog DTO ─────────────────────────────────────────────────────

export const PromptLlmModelTierSchema = z.enum(['economy', 'standard', 'premium']);
export type PromptLlmModelTier = z.infer<typeof PromptLlmModelTierSchema>;

export const PromptLlmModelProviderSchema = z.enum(['anthropic', 'google']);
export type PromptLlmModelProvider = z.infer<typeof PromptLlmModelProviderSchema>;

export const PromptLlmModelFamilySchema = z.enum(['claude', 'gemini']);
export type PromptLlmModelFamily = z.infer<typeof PromptLlmModelFamilySchema>;

export const PromptLlmModelCostHintSchema = z.enum(['$', '$$', '$$$']);
export type PromptLlmModelCostHint = z.infer<typeof PromptLlmModelCostHintSchema>;

export const PromptLlmModelSchema = z.object({
  id: z.string().min(1).max(100),
  label: z.string().min(1),
  provider: PromptLlmModelProviderSchema,
  family: PromptLlmModelFamilySchema,
  tier: PromptLlmModelTierSchema,
  costHint: PromptLlmModelCostHintSchema,
  capabilities: z.array(PromptTaskTypeSchema).min(1),
  /**
   * Vertex advertises this id but the project cannot invoke it. Such a model is
   * still listed, locked, so "why is Opus 5 missing?" has an answer in the UI
   * instead of in someone's head. Absent means usable.
   */
  unavailable: z.boolean().optional(),
});
export type PromptLlmModel = z.infer<typeof PromptLlmModelSchema>;

export const PromptLlmModelsResponseSchema = z.object({
  models: z.array(PromptLlmModelSchema),
  /** Soft cache age marker for UI freshness hints. */
  fetchedAt: z.string().datetime().nullable(),
  /** True when serving bootstrap fallback because Vertex returned nothing usable. */
  fromBootstrap: z.boolean(),
});
export type PromptLlmModelsResponse = z.infer<typeof PromptLlmModelsResponseSchema>;

// ─── Family helpers ──────────────────────────────────────────────────

export function promptModelFamily(modelId: string): PromptLlmModelFamily | null {
  const id = modelId.trim().toLowerCase();
  if (id.startsWith('claude-')) return 'claude';
  if (id.startsWith('gemini-')) return 'gemini';
  return null;
}

export function isClaudePromptModel(modelId: string | null | undefined): boolean {
  return modelId != null && promptModelFamily(modelId) === 'claude';
}

export function isGeminiPromptModel(modelId: string | null | undefined): boolean {
  return modelId != null && promptModelFamily(modelId) === 'gemini';
}

/**
 * Prefer a saved prompt model when its family matches the call site.
 * Otherwise return the service fallback (expand/contract).
 */
export function resolvePromptModelForFamily(
  promptModel: string | null | undefined,
  expectedFamily: PromptLlmModelFamily,
  fallbackModel: string,
): string {
  if (promptModel && promptModelFamily(promptModel) === expectedFamily) {
    return promptModel;
  }
  return fallbackModel;
}

// ─── Tier heuristics (id-based; not Vertex pricing) ─────────────────

export function derivePromptModelTier(modelId: string): PromptLlmModelTier {
  const id = modelId.toLowerCase();
  if (id.includes('opus') || id.includes('pro') || id.includes('ultra')) return 'premium';
  if (id.includes('flash-lite') || id.includes('haiku') || id.includes('flash')) return 'economy';
  if (id.includes('sonnet') || id.includes('gemini')) return 'standard';
  return 'standard';
}

export function costHintForTier(tier: PromptLlmModelTier): PromptLlmModelCostHint {
  if (tier === 'economy') return '$';
  if (tier === 'premium') return '$$$';
  return '$$';
}

// ─── Bootstrap fallback (prod IDs Nexus already runs) ────────────────

function bootstrapModel(
  id: string,
  label: string,
  provider: PromptLlmModelProvider,
  family: PromptLlmModelFamily,
): PromptLlmModel {
  const tier = derivePromptModelTier(id);
  const capabilities = derivePromptModelCapabilities(id);
  if (capabilities.length === 0) {
    throw new Error(`Bootstrap model ${id} has no capabilities`);
  }
  return {
    id,
    label,
    provider,
    family,
    tier,
    costHint: costHintForTier(tier),
    capabilities,
  };
}

export const PROMPT_LLM_BOOTSTRAP_MODELS: readonly PromptLlmModel[] = [
  bootstrapModel('claude-opus-4-6', 'Claude Opus 4.6', 'anthropic', 'claude'),
  bootstrapModel('claude-opus-4-5@20251101', 'Claude Opus 4.5', 'anthropic', 'claude'),
  bootstrapModel('gemini-2.5-flash', 'Gemini 2.5 Flash', 'google', 'gemini'),
  bootstrapModel('gemini-2.0-flash', 'Gemini 2.0 Flash', 'google', 'gemini'),
  bootstrapModel('gemini-3-pro-image', 'Gemini 3 Pro Image', 'google', 'gemini'),
  bootstrapModel('gemini-3.1-flash-lite-image', 'Gemini 3.1 Flash Lite Image', 'google', 'gemini'),
];

// ─── Smart suggestions by real usedIn ────────────────────────────────

export const PromptModelSuggestionSchema = z.object({
  provider: PromptLlmModelProviderSchema,
  recommendedTier: PromptLlmModelTierSchema,
  suggestedModelId: z.string().min(1).max(100).optional(),
});
export type PromptModelSuggestion = z.infer<typeof PromptModelSuggestionSchema>;

const CLAUDE_PREMIUM: PromptModelSuggestion = {
  provider: 'anthropic',
  recommendedTier: 'premium',
  suggestedModelId: 'claude-opus-4-6',
};

const GEMINI_ECONOMY: PromptModelSuggestion = {
  provider: 'google',
  recommendedTier: 'economy',
  suggestedModelId: 'gemini-2.5-flash',
};

const GEMINI_IMAGE_PREMIUM: PromptModelSuggestion = {
  provider: 'google',
  recommendedTier: 'premium',
  suggestedModelId: 'gemini-3-pro-image',
};

const GEMINI_IMAGE_ECONOMY: PromptModelSuggestion = {
  provider: 'google',
  recommendedTier: 'economy',
  suggestedModelId: 'gemini-3.1-flash-lite-image',
};

/**
 * Per-usedIn model guidance for the Prompts editor.
 * Seeded from call-site defaults — not from PROMPT_USED_IN_SUGGESTIONS (legacy UI labels).
 */
export const PROMPT_MODEL_SUGGESTIONS: Readonly<Record<string, PromptModelSuggestion>> = {
  'opportunity-generation-premise': GEMINI_ECONOMY,
  'opportunity-generation-framework': GEMINI_ECONOMY,
  'creative-script-generation-angles': CLAUDE_PREMIUM,
  'creative-script-generation-topics': CLAUDE_PREMIUM,
  'creative-script-generation-custom': CLAUDE_PREMIUM,
  [PERSONA_SCRIPT_IMPORT_PROMPT_USED_IN]: CLAUDE_PREMIUM,
  'creative-brief-edit-persona-angles': CLAUDE_PREMIUM,
  'creative-script-hook-variations-persona': CLAUDE_PREMIUM,
  'creative-script-edit-meta-prompt': CLAUDE_PREMIUM,
  'creative-script-edit-feedback-message': CLAUDE_PREMIUM,
  'creative-storyboard-beat-extraction': GEMINI_ECONOMY,
  'creative-storyboard-image': GEMINI_IMAGE_ECONOMY,
  'persona-avatar-generation': GEMINI_IMAGE_PREMIUM,
  [PERSONA_REFERENCE_VIDEO_ANALYSIS_PROMPT_USED_IN]: GEMINI_ECONOMY,
  [SCRIPT_REVIEW_PROMPT_TRANSCRIPT]: GEMINI_ECONOMY,
  [SCRIPT_REVIEW_PROMPT_VOICEOVER]: GEMINI_ECONOMY,
  [SCRIPT_REVIEW_PROMPT_TEXT_OVERLAY]: GEMINI_ECONOMY,
  [SCRIPT_REVIEW_PROMPT_VISUAL]: GEMINI_ECONOMY,
  [SCRIPT_REVIEW_PROMPT_SCRIPT_EXTRACTION]: GEMINI_ECONOMY,
  [SCRIPT_REVIEW_PROMPT_SUGGESTION]: GEMINI_ECONOMY,
  [SCRIPT_REVIEW_PROMPT_SCRIPT_MATCH]: GEMINI_ECONOMY,
  [SCRIPT_REVIEW_PROMPT_ITERATION_VOICEOVER]: GEMINI_ECONOMY,
  [SCRIPT_REVIEW_PROMPT_ITERATION_TEXT_OVERLAY]: GEMINI_ECONOMY,
  [SCRIPT_REVIEW_PROMPT_ITERATION_VISUAL]: GEMINI_ECONOMY,
  [CHANGELOG_PROMPT_DEPLOY_NOTES]: GEMINI_ECONOMY,
  [CHANGELOG_PROMPT_ROUNDUP]: GEMINI_ECONOMY,
};

export function getPromptModelSuggestion(usedIn: string): PromptModelSuggestion | null {
  return PROMPT_MODEL_SUGGESTIONS[usedIn] ?? null;
}

const TIER_RANK: Record<PromptLlmModelTier, number> = {
  economy: 0,
  standard: 1,
  premium: 2,
};

export function comparePromptModelTiers(a: PromptLlmModelTier, b: PromptLlmModelTier): number {
  return TIER_RANK[a] - TIER_RANK[b];
}

/**
 * Resolve a suggestion against the live catalog: prefer suggestedModelId if still
 * listed and capable; otherwise newest matching provider + recommendedTier that
 * supports the required task type (when provided).
 */
export function resolvePromptModelSuggestion(
  usedIn: string,
  catalog: readonly PromptLlmModel[],
  taskType?: PromptTaskType,
): PromptLlmModel | null {
  const suggestion = getPromptModelSuggestion(usedIn);
  if (!suggestion) return null;

  const capable = (m: PromptLlmModel) =>
    taskType == null || m.capabilities.includes(taskType);

  if (suggestion.suggestedModelId) {
    const exact = catalog.find((m) => m.id === suggestion.suggestedModelId && capable(m));
    if (exact) return exact;
  }

  const matches = catalog.filter(
    (m) =>
      m.provider === suggestion.provider &&
      m.tier === suggestion.recommendedTier &&
      capable(m),
  );
  if (matches.length === 0) return null;

  // Prefer lexicographically last id as a crude "newest" heuristic for versioned ids.
  return [...matches].sort((a, b) => a.id.localeCompare(b.id)).at(-1) ?? null;
}
